import { Tooltip } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";

/**
 * Stacking order for the tooltip popper.
 *
 * MUI's default is 1500, which loses to every modal in this app: the form modals
 * sit at 125666 (assets/styles/modelForm.scss), some overlays at 555555, and the
 * delete confirmation at 666666. Since these tooltips are used INSIDE those
 * modals, the popper has to clear all of them or it renders behind the form and
 * looks like nothing happened.
 *
 * The popper is portalled to document.body, so this competes with the whole
 * app's stacking contexts, not just the local one.
 */
const TOOLTIP_Z_INDEX = 1000000;

/**
 * An "ⓘ" affordance that explains a field on hover.
 *
 * WHY THIS EXISTS: settings forms accumulate explanatory text under every
 * control, and that text is useful the first two times someone reads it. After
 * that it is noise that pushes the save button below the fold. Putting the
 * explanation behind an icon keeps the form scannable — a column of labels and
 * switches — while leaving the detail one hover away.
 *
 * Opens instantly (no enter delay) because a help icon that needs to be held is
 * a help icon nobody reads. Touch and keyboard focus also open it, so the
 * explanation is reachable without a mouse.
 *
 * @param {object} props
 * @param {import("react").ReactNode} props.children - The explanation. Keep it
 *   to a sentence or two; anything longer belongs in the plan docs.
 * @param {"top"|"bottom"|"left"|"right"} [props.placement="top"] - Tooltip side.
 *   Leave at the default in RTL forms — "top" sidesteps left/right mirroring.
 * @param {string} [props.label="شرح"] - Accessible name for the trigger.
 */
const FieldHelp = ({ children, placement = "top", label = "شرح" }) => (
    <Tooltip
        placement={placement}
        arrow
        // Instant in, brief grace period out so the pointer can cross the gap
        // between the icon and the bubble without it vanishing mid-sentence.
        enterDelay={0}
        enterTouchDelay={0}
        leaveDelay={100}
        title={
            <span style={{ fontSize: "0.8rem", lineHeight: 1.7, display: "block" }}>
                {children}
            </span>
        }
        slotProps={{
            popper: { sx: { zIndex: TOOLTIP_Z_INDEX } },
            tooltip: { sx: { maxWidth: 260, textAlign: "right" } },
        }}
    >
        <button
            type="button"
            aria-label={label}
            // Inside a <form>; without type="button" this submits it.
            onClick={(e) => e.preventDefault()}
            style={{
                background: "none",
                border: "none",
                padding: 0,
                marginInlineStart: "6px",
                cursor: "help",
                lineHeight: 1,
                color: "#9aa0a6",
                display: "inline-flex",
                alignItems: "center",
            }}
        >
            <FontAwesomeIcon icon={faCircleInfo} size="sm" />
        </button>
    </Tooltip>
);

export default FieldHelp;
